import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound } from 'lucide-react'
import { Link } from '@/components/common/CustomLink'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputPassword } from '@/components/ui/input-password'
import { Text } from '@/components/ui/text'
import { Checkbox } from '@/components/ui/checkbox'
import { useLoading } from '@/contexts/LoadingContext'
import { AuthenticationLayout } from '@/components/layout/AuthenticationLayout.tsx'
import { cn } from '@/lib/utils'

type LoginFormData = {
  email: string
  password: string
  rememberMe?: boolean
}

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<void>
  isLoading?: boolean
  error?: string
}

export function LoginForm({
  onSubmit,
  isLoading = false,
  error,
}: LoginFormProps) {
  const [rememberMe, setRememberMe] = useState(false)
  const { t } = useTranslation()
  const { showLoading, hideLoading } = useLoading()

  useEffect(() => {
    if (isLoading) {
      showLoading(t('auth.signingIn'))
    } else {
      hideLoading()
    }
  }, [isLoading, showLoading, hideLoading, t])

  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t('validation.emailRequired'))
      .email(t('validation.emailInvalid')),
    password: z.string().min(1, t('validation.passwordRequired')),
    rememberMe: z.boolean().optional(),
  })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  })

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked)
    setValue('rememberMe', checked)
  }
  return (
    <>
      <AuthenticationLayout
        icon={<KeyRound className='w-6 h-6 text-dark-600' />}
        title={t('auth.loginTitle')}
        description={t('auth.loginDescription')}
      >
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='space-y-2'>
            <Text
              as='label'
              htmlFor='email'
              className='text-sm font-normal'
              variant='formLabel'
            >
              {t('common.email')}
            </Text>
            <Input
              id='email'
              type='text'
              placeholder={t('auth.enterEmail')}
              className={cn(
                'h-11 bg-white',
                (errors.email || error) && 'border-red-500',
                !(errors.email || error) && 'border-dark-300'
              )}
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && (
              <p className='text-sm text-red-600'>{errors.email.message}</p>
            )}
          </div>

          <div className='space-y-2'>
            <Text
              as='label'
              htmlFor='password'
              className='text-sm font-normal'
              variant='formLabel'
            >
              {t('common.password')}
            </Text>
            <InputPassword
              id='password'
              placeholder={t('auth.enterPassword')}
              className={cn(
                'h-11 bg-white',
                (errors.password || error) && 'border-red-500',
                !(errors.password || error) && 'border-dark-300'
              )}
              {...register('password')}
              disabled={isLoading}
              autoComplete='password'
            />
            {(errors.password || error) && (
              <p className='text-sm text-red-600'>
                {errors.password?.message || t('auth.invalidCredentials')}
              </p>
            )}
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='rememberMe'
                checked={rememberMe}
                onCheckedChange={checked =>
                  handleRememberMeChange(checked as boolean)
                }
                disabled={isLoading}
              />
              <label
                htmlFor='rememberMe'
                className='text-sm text-dark-600 cursor-pointer select-none'
              >
                {t('auth.rememberMe')}
              </label>
            </div>
            <Link to='/forgot' className='text-sm font-bold hover:underline'>
              {t('auth.forgotPassword')}?
            </Link>
          </div>

          <Button
            type='submit'
            className='w-full h-11 font-medium'
            disabled={isLoading}
          >
            {t('auth.login')}
          </Button>
        </form>
      </AuthenticationLayout>
    </>
  )
}
